import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Eye,
  LayoutTemplate,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import PageTitleBlock from '../components/PageTitleBlock';
import { CONTENT_TAXONOMY_SCOPES, getContentTaxonomies } from '../contentTaxonomyApi';
import { videoFirstFrameSource } from '../mediaPreview';
import { deleteProjectTemplate, getProjectTemplates } from './templateApi';

const PREVIEW_TEMPLATES = [
  { id: 1, type: 'script', type_name: '剧本模板', title: '高概念短剧 · 12 集', description: '从人物欲望、核心冲突到每集钩子，快速搭好短剧骨架。', scope: 'official' },
  { id: 2, type: 'canvas', type_name: '画布模板', title: '产品广告视觉板', description: '包含产品特写、环境镜头与节奏参考节点。', scope: 'official' },
  { id: 3, type: 'storyboard', type_name: '分镜模板', title: '情绪短片分镜', description: '以情绪曲线组织近景、空镜与转场。', scope: 'official' },
];

function templateTypesFromItems(items) {
  return [...new Map(items.map(item => [item.type, { code: item.type, name: item.type_name || item.type }])).values()];
}

function TemplateCard({ item, typeName, scope, busy, onPreview, onDelete }) {
  return (
    <article className="group flex min-h-[224px] flex-col overflow-hidden rounded-[18px] border border-black/[0.07] bg-white/66 shadow-[0_18px_48px_-42px_rgba(41,35,47,.45)] transition hover:-translate-y-0.5 hover:border-[#8a72a4]/20 hover:bg-white dark:border-white/[0.075] dark:bg-white/[0.035] dark:hover:bg-white/[0.055]">
      <div className="relative aspect-[16/8] overflow-hidden bg-[linear-gradient(135deg,#ece5f3,#d9e2ea)] dark:bg-[linear-gradient(135deg,#332b3d,#24313a)]">
        {item.cover ? <img src={item.cover} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.025]" /> : item.preview_url ? <video src={videoFirstFrameSource(item.preview_url)} poster={item.preview_poster || undefined} muted playsInline preload="metadata" aria-hidden="true" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.025]" /> : <LayoutTemplate className="absolute bottom-4 right-4 text-white/62" size={42} strokeWidth={1.15} />}
        <span className="absolute left-3 top-3 rounded-full border border-white/25 bg-black/30 px-2.5 py-1 text-[9px] font-medium text-white backdrop-blur-md">{item.type_name || typeName || item.type}</span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h2 className="line-clamp-1 text-[14px] font-semibold tracking-[-0.02em] text-stone-900 dark:text-white/90">{item.title}</h2>
        <p className="mt-2 line-clamp-2 text-[11px] leading-5 text-stone-500 dark:text-white/42">{item.description || '尚未填写模板说明'}</p>
        <div className="mt-auto flex items-center justify-end gap-2 pt-4">
          {scope === 'mine' && (
            <button type="button" disabled={busy === `delete-${item.id}`} onClick={() => onDelete(item)} className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-black/[0.07] px-2.5 text-[10px] text-stone-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-55 dark:border-white/[0.08] dark:hover:bg-red-500/10">
              {busy === `delete-${item.id}` ? <Loader2 className="animate-spin" size={12} /> : <Trash2 size={12} />} 删除
            </button>
          )}
          <button type="button" onClick={() => onPreview(item)} className="inline-flex h-8 items-center gap-1.5 rounded-[8px] bg-[#352f3b] px-3 text-[10px] font-medium text-white transition hover:bg-[#29242f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8a72a4]/45 dark:bg-[#c1afe3] dark:text-[#241e29]">
            <Eye size={12} /> 查看模板
          </button>
        </div>
      </div>
    </article>
  );
}

export default function TemplateLibraryPage({ preview: previewProp = false }) {
  const outlet = useOutletContext() || {};
  const preview = previewProp || outlet.preview;
  const navigate = useNavigate();
  const [scope, setScope] = useState(() => (
    new URLSearchParams(window.location.search).get('scope') === 'mine' ? 'mine' : 'official'
  ));
  const [type, setType] = useState('all');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState(preview ? PREVIEW_TEMPLATES : []);
  const [templateTypes, setTemplateTypes] = useState(() => preview ? templateTypesFromItems(PREVIEW_TEMPLATES) : []);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');

  const loadTypes = useCallback(async () => {
    if (preview) return;
    try {
      const nextTypes = await getContentTaxonomies(CONTENT_TAXONOMY_SCOPES.templates);
      setTemplateTypes(nextTypes);
      setType(current => current === 'all' || nextTypes.some(item => item.code === current) ? current : 'all');
    } catch (requestError) {
      setTemplateTypes([]);
      setError(getErrorMessage(requestError, '模板类型加载失败'));
    }
  }, [preview]);

  const load = useCallback(async () => {
    if (preview) return;
    setLoading(true);
    setError('');
    try {
      const data = await getProjectTemplates(scope, { type, q: query.trim() || undefined });
      setItems(Array.isArray(data.list) ? data.list : []);
    } catch (requestError) {
      setItems([]);
      setError(getErrorMessage(requestError, '模板加载失败'));
    } finally {
      setLoading(false);
    }
  }, [preview, query, scope, type]);

  useEffect(() => {
    const timer = window.setTimeout(load, query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  const visibleItems = useMemo(() => {
    if (!preview) return items;
    const keyword = query.trim().toLocaleLowerCase('zh-CN');
    return items.filter(item => (type === 'all' || item.type === type) && (!keyword || `${item.title} ${item.description}`.toLocaleLowerCase('zh-CN').includes(keyword)));
  }, [items, preview, query, type]);

  const openTemplatePreview = item => navigate(preview
    ? `/prototype/vnext/templates/${item.id}`
    : `/templates/${item.id}`);

  const removeTemplate = async item => {
    if (!window.confirm(`确认删除“${item.title}”？删除后不能恢复。`)) return;
    setBusy(`delete-${item.id}`);
    setError('');
    setNotice('');
    try {
      if (!preview) await deleteProjectTemplate(item.id);
      setItems(current => current.filter(candidate => candidate.id !== item.id));
      setNotice('模板已删除');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '模板删除失败'));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="vnext-page-surface min-h-full">
      <div className="vnext-page-frame pb-24">
        <div className="flex flex-col gap-5 pt-9 sm:pt-0 md:flex-row md:items-end md:justify-between">
          <PageTitleBlock title="模板库" />
          <label className="flex h-10 w-full items-center gap-2 rounded-[11px] border border-black/[0.07] bg-white/60 px-3 md:w-[300px] dark:border-white/[0.08] dark:bg-white/[0.035]">
            <Search size={14} className="text-stone-400" />
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索模板名称或说明" className="min-w-0 flex-1 bg-transparent text-[11px] outline-none placeholder:text-stone-400" />
          </label>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-black/[0.065] pb-4 dark:border-white/[0.07]">
          <div className="flex rounded-[11px] bg-black/[0.035] p-1 dark:bg-white/[0.045]" role="tablist" aria-label="模板来源">
            {[['official', '官方模板'], ['mine', '我的模板']].map(([value, label]) => <button type="button" role="tab" aria-selected={scope === value} key={value} onClick={() => { setScope(value); setType('all'); setNotice(''); }} className={`h-8 rounded-[8px] px-3 text-[11px] font-medium transition ${scope === value ? 'bg-white text-stone-900 shadow-sm dark:bg-white/[0.12] dark:text-white' : 'text-stone-500 dark:text-white/42'}`}>{label}</button>)}
          </div>
          <div className="flex max-w-full gap-1.5 overflow-x-auto [scrollbar-width:none]" role="radiogroup" aria-label="模板类型">
            {[{ code: 'all', name: '全部模板' }, ...templateTypes].map(option => <button type="button" role="radio" aria-checked={type === option.code} key={option.code} onClick={() => setType(option.code)} className={`h-8 shrink-0 rounded-full border px-3 text-[10px] transition ${type === option.code ? 'border-[#7f6997]/18 bg-[#352f3b] text-white dark:bg-[#c1afe3] dark:text-[#241e29]' : 'border-black/[0.07] bg-white/45 text-stone-500 dark:border-white/[0.08] dark:bg-white/[0.025] dark:text-white/42'}`}>{option.name}</button>)}
          </div>
        </div>

        {notice && <div className="mt-4 flex items-center justify-between rounded-[10px] bg-emerald-600/[0.08] px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-300" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice('')}>关闭</button></div>}
        {error && <div className="mt-4 flex items-center justify-between rounded-[10px] bg-red-500/[0.08] px-3 py-2 text-[11px] text-red-700 dark:text-red-300" role="alert"><span>{error}</span><button type="button" onClick={() => { loadTypes(); load(); }} className="inline-flex items-center gap-1"><RefreshCw size={12} />重试</button></div>}

        <section className="mt-5" aria-label={scope === 'official' ? '官方模板列表' : '我的模板列表'}>
          {loading ? <div className="grid min-h-64 place-items-center text-[11px] text-stone-400"><span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={15} />正在加载模板…</span></div> : visibleItems.length ? <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{visibleItems.map(item => <TemplateCard key={item.id} item={item} typeName={templateTypes.find(candidate => candidate.code === item.type)?.name} scope={scope} busy={busy} onPreview={openTemplatePreview} onDelete={removeTemplate} />)}</div> : <div className="grid min-h-64 place-items-center text-center"><div><LayoutTemplate className="mx-auto text-stone-300 dark:text-white/18" size={30} /><h2 className="mt-3 text-[15px] font-semibold">{scope === 'official' ? '当前分类暂无官方模板' : '还没有保存自己的模板'}</h2><p className="mt-2 text-[11px] text-stone-500 dark:text-white/38">{scope === 'official' ? '运营上架模板后会显示在这里。' : '从画布“另存为模板”后，可以在这里预览和再次克隆。'}</p></div></div>}
        </section>
      </div>
    </div>
  );
}
